import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import LoadingState from '@/components/ui/LoadingState';
import { getHomePath, getRoutePermission, hasPermission } from './constants/auth';
import { useAuthStore } from './stores/authStore';

const Login = lazy(() => import('./pages/Login'));
const Layout = lazy(() => import('./components/Layout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const OrderForm = lazy(() => import('./pages/OrderForm'));
const DesignCenter = lazy(() => import('./pages/DesignCenter'));
const DesignWorkspace = lazy(() => import('./pages/DesignWorkspace'));
const Customers = lazy(() => import('./pages/Customers'));
const Production = lazy(() => import('./pages/Production'));
const Inventory = lazy(() => import('./pages/Inventory'));
const BOMs = lazy(() => import('./pages/BOMs'));
const Wages = lazy(() => import('./pages/Wages'));
const BomAudits = lazy(() => import('./pages/BomAudits'));
const Settings = lazy(() => import('./pages/Settings'));
const BusinessHome = lazy(() => import('./pages/business/BusinessHome'));
const BusinessCustomerList = lazy(() => import('./pages/business/BusinessCustomerList'));
const BusinessCustomerDetail = lazy(() => import('./pages/business/BusinessCustomerDetail'));
const BusinessCustomerForm = lazy(() => import('./pages/business/BusinessCustomerForm'));
const AdminCustomerOwnership = lazy(() => import('./pages/admin/AdminCustomerOwnership'));
const BusinessNotifications = lazy(() => import('./pages/business/BusinessNotifications'));
const BusinessOrderWizard = lazy(() => import('./pages/business/BusinessOrderWizard'));
const BusinessOrderList = lazy(() => import('./pages/business/BusinessOrderList'));
const BusinessOrderDetail = lazy(() => import('./pages/business/BusinessOrderDetail'));
const AdminChangeRequestApproval = lazy(() => import('./pages/admin/AdminChangeRequestApproval'));
const AdminCommissionRules = lazy(() => import('./pages/admin/AdminCommissionRules'));
const BusinessCommission = lazy(() => import('./pages/business/BusinessCommission'));
const BusinessProfile = lazy(() => import('./pages/business/BusinessProfile'));
const AdminScreen = lazy(() => import('./pages/AdminScreen'));
const DeliveryCenter = lazy(() => import('./pages/DeliveryCenter'));
const DingTalkRobot = lazy(() => import('./pages/DingTalkRobot'));
const ShenluCrateCalculator = lazy(() => import('./pages/ShenluCrateCalculator'));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <LoadingState message="页面加载中..." compact rows={2} />
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const permission = getRoutePermission(location.pathname);
  if (permission && !hasPermission(user, permission)) {
    return <Navigate to={getHomePath(user)} replace />;
  }

  return <>{children}</>;
}

function InternalOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role === 'client') {
    return <Navigate to="/orders" replace />;
  }
  return <>{children}</>;
}

function HomeRedirect() {
  const { user } = useAuthStore();
  return <Navigate to={getHomePath(user)} replace />;
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <LazyPage>
            <Login />
          </LazyPage>
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <LazyPage>
              <Layout />
            </LazyPage>
          </PrivateRoute>
        }
      >
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={<LazyPage><Dashboard /></LazyPage>} />
        <Route path="admin/screen" element={<LazyPage><AdminScreen /></LazyPage>} />
        <Route path="business/home" element={<LazyPage><BusinessHome /></LazyPage>} />
        <Route path="notifications" element={<LazyPage><BusinessNotifications /></LazyPage>} />
        <Route path="business/notifications" element={<LazyPage><BusinessNotifications /></LazyPage>} />
        <Route path="business/order" element={<LazyPage><BusinessOrderList /></LazyPage>} />
        <Route path="business/order/new" element={<LazyPage><BusinessOrderWizard /></LazyPage>} />
        <Route path="business/order/:id/edit" element={<LazyPage><BusinessOrderWizard /></LazyPage>} />
        <Route path="business/order/:id" element={<LazyPage><BusinessOrderDetail /></LazyPage>} />
        <Route path="business/customer" element={<LazyPage><BusinessCustomerList /></LazyPage>} />
        <Route path="business/customer/new" element={<LazyPage><BusinessCustomerForm /></LazyPage>} />
        <Route path="business/customer/:id" element={<LazyPage><BusinessCustomerDetail /></LazyPage>} />
        <Route path="business/customer/:id/edit" element={<LazyPage><BusinessCustomerForm /></LazyPage>} />
        <Route path="admin/customer-ownership" element={<LazyPage><AdminCustomerOwnership /></LazyPage>} />
        <Route path="admin/change-requests" element={<LazyPage><AdminChangeRequestApproval /></LazyPage>} />
        <Route path="admin/commission-rules" element={<LazyPage><AdminCommissionRules /></LazyPage>} />
        <Route path="business/commission" element={<LazyPage><BusinessCommission /></LazyPage>} />
        <Route path="business/dashboard" element={<LazyPage><BusinessProfile /></LazyPage>} />
        <Route path="business/profile" element={<Navigate to="/business/dashboard" replace />} />
        <Route path="orders" element={<LazyPage><Orders /></LazyPage>} />
        <Route path="orders/new" element={<InternalOnlyRoute><LazyPage><OrderForm mode="create" /></LazyPage></InternalOnlyRoute>} />
        <Route path="orders/:id" element={<LazyPage><OrderDetail /></LazyPage>} />
        <Route path="orders/:id/edit" element={<InternalOnlyRoute><LazyPage><OrderForm mode="edit" /></LazyPage></InternalOnlyRoute>} />
        <Route path="design" element={<LazyPage><DesignCenter /></LazyPage>} />
        <Route path="design/:id" element={<LazyPage><DesignWorkspace /></LazyPage>} />
        <Route path="customers" element={<LazyPage><Customers /></LazyPage>} />
        <Route path="production" element={<LazyPage><Production /></LazyPage>} />
        <Route path="delivery" element={<LazyPage><DeliveryCenter /></LazyPage>} />
        <Route path="dingtalk-robot" element={<LazyPage><DingTalkRobot /></LazyPage>} />
        <Route path="tools/crate-calculator" element={<LazyPage><ShenluCrateCalculator /></LazyPage>} />
        <Route path="inventory" element={<LazyPage><Inventory /></LazyPage>} />
        <Route path="boms" element={<LazyPage><BOMs /></LazyPage>} />
        <Route path="wages" element={<LazyPage><Wages /></LazyPage>} />
        <Route path="bom-audits" element={<LazyPage><BomAudits /></LazyPage>} />
        <Route path="settings" element={<LazyPage><Settings /></LazyPage>} />
      </Route>
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

export default App;
